/**
 * Customer-facing words for backend values. Presentation only: statuses and codes come from the
 * backend; this file decides how they read. Unknown values fall back to a readable form of the value.
 */
import { asCartRevisionConflict, isApiError } from '@/api/errors';
import type { BadgeTone } from '@/components/Badge';
import { describeError } from '@/lib/describe-error';
import type { ErrorDescription } from '@/lib/describe-error';
import type { LocationType } from '@/venue/venue';
import { venueVocabulary } from '@/venue/venue';
import type { AssistanceType, Cart } from './api';

/** "ORD-0007" → "Order #0007". Any other form is shown as the backend sent it. */
export function orderTitle(orderNumber: string): string {
  const match = /^ORD-(\d+)$/.exec(orderNumber);
  return match ? `Order #${match[1]}` : `Order ${orderNumber}`;
}

function humanize(value: string): string {
  const text = value.replace(/_/g, ' ').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

type Tone = Exclude<BadgeTone, 'waiter'>;

const ORDER_STATUS: Record<string, { label: string; tone: Tone }> = {
  SUBMITTED: { label: 'Received', tone: 'neutral' },
  IN_PREPARATION: { label: 'Preparing', tone: 'primary' },
  READY: { label: 'Ready', tone: 'success' },
  SERVED: { label: 'Served', tone: 'neutral' },
  CANCELLED: { label: 'Cancelled', tone: 'danger' },
};

/** Label and tone for an order or item status. Never violet: that is reserved for "Added by waiter". */
export function orderStatusView(
  status: string,
  locationType: LocationType,
): { label: string; tone: Tone } {
  const known = ORDER_STATUS[status];
  if (!known) return { label: humanize(status), tone: 'neutral' };
  if (status === 'SERVED') return { ...known, label: venueVocabulary(locationType).served };
  return known;
}

/** The backend allows guest cancellation only while nothing has started; that is the SUBMITTED status. */
export function canRequestCancel(orderStatus: string): boolean {
  return orderStatus === 'SUBMITTED';
}

const SETTLEMENT: Record<string, { label: string; tone: Tone }> = {
  PENDING: { label: 'Balance due', tone: 'attention' },
  SETTLED: { label: 'Paid', tone: 'success' },
  CLOSED: { label: 'Closed', tone: 'neutral' },
};

export function settlementView(status: string): { label: string; tone: Tone } {
  return SETTLEMENT[status] ?? { label: humanize(status), tone: 'neutral' };
}

const TENDER: Record<string, string> = { GATEWAY_ONLINE: 'Online payment' };
export const tenderLabel = (tender: string) => TENDER[tender] ?? humanize(tender);

/** Follows the tender in one line: "Online payment · not completed". */
const PAYMENT_STATUS: Record<string, string> = {
  SETTLED: 'received',
  PENDING: 'not completed',
  FAILED: 'failed',
};
export const paymentStatusLabel = (status: string) =>
  PAYMENT_STATUS[status] ?? humanize(status).toLowerCase();

export const ASSISTANCE_LABEL: Record<AssistanceType, { title: string; hint: string }> = {
  CALL_WAITER: { title: 'A waiter', hint: 'Someone will come to you.' },
  WATER: { title: 'Water', hint: 'A jug or bottles for the table.' },
  CUTLERY: { title: 'Cutlery', hint: 'Cutlery, plates or napkins.' },
  BILL: { title: 'The bill', hint: 'Staff bring the bill to you.' },
  OTHER: { title: 'Something else', hint: 'Tell us what you need below.' },
};

/**
 * What changed between the cart the customer last saw and the cart now, in words. Null when nothing
 * changed. Revision numbers are compared but never shown.
 */
export function describeCartChange(previous: Cart, next: Cart): string | null {
  if (previous.revision === next.revision) return null;
  const known = new Set(previous.items.map((line) => line.cart_item_id));
  const added = next.items.filter(
    (line) => line.added_by === 'WAITER' && !known.has(line.cart_item_id),
  );
  if (added.length === 1) {
    const line = added[0]!;
    return `Your waiter added ${line.name ?? 'an item'} ×${line.quantity}.`;
  }
  if (added.length > 1) return `Your waiter added ${added.length} items.`;
  return 'Your cart was updated.';
}

/** The approved wording for a cart that changed after it was reviewed. */
export function conflictMessage(reviewed: Cart, latest: Cart | undefined): string {
  const waiterAdded =
    latest !== undefined &&
    latest.items.some(
      (line) =>
        line.added_by === 'WAITER' &&
        !reviewed.items.some((old) => old.cart_item_id === line.cart_item_id),
    );
  return waiterAdded
    ? 'Your waiter added an item. Please review your cart before placing the order.'
    : 'Please review your cart before placing the order.';
}

const BY_CODE: Record<string, { title: string; message: string }> = {
  INVALID_QR_TOKEN: {
    title: "This QR code isn't valid",
    message: 'Scan the QR code again, or ask a staff member for help.',
  },
  ITEM_OUT_OF_STOCK: {
    title: 'No longer available',
    message: 'An item is not available right now. Please remove it or choose something else.',
  },
  CART_EMPTY: { title: 'Your cart is empty', message: 'Add something from the menu first.' },
  CART_ITEM_NOT_FOUND: {
    title: 'That item is no longer in your cart',
    message: 'Your cart has been refreshed.',
  },
  SESSION_ALREADY_CLOSED: {
    title: 'This session is closed',
    message: 'Scan the QR code again to start a new one, or ask a staff member.',
  },
  SESSION_NOT_FOUND: {
    title: "We couldn't find your session",
    message: 'Scan the QR code again to continue.',
  },
  ORDER_NOT_CANCELLABLE: {
    title: "This order can't be cancelled here",
    message: 'The kitchen has already started it. Please ask a staff member.',
  },
  ORDER_NOT_FOUND: { title: 'Order not found', message: "We couldn't find that order." },
  PAY_BEFORE_ORDER_NOT_SUPPORTED: {
    title: "Ordering isn't available here yet",
    message: 'Please ask a staff member to take your order.',
  },
  DUPLICATE_IDEMPOTENCY_KEY: {
    title: "That didn't go through",
    message: 'Please try again.',
  },
};

/** Words for any error a customer screen can meet. Never a code, a status number or a backend message. */
export function describeCustomerError(
  error: unknown,
  locationType?: LocationType,
): ErrorDescription {
  if (!isApiError(error)) return describeError(error);
  if (asCartRevisionConflict(error)) return describeError(error);

  if (error.hasCode('TABLE_CAPACITY_EXCEEDED')) {
    const noun = (
      locationType ? venueVocabulary(locationType).locationNoun : 'Table'
    ).toLowerCase();
    return {
      title:
        locationType === 'SEAT' ? 'This seat is already in use' : "There isn't room at this table",
      message:
        locationType === 'SEAT'
          ? 'Please check your seat number, or ask a staff member.'
          : `This ${noun} can't take that many more guests. Please ask a staff member.`,
      reference: error.correlationId,
    };
  }
  for (const [code, words] of Object.entries(BY_CODE)) {
    if (error.hasCode(code)) return { ...words, reference: error.correlationId };
  }
  if (error.hasCode('VALIDATION_ERROR')) {
    return {
      title: "That didn't go through",
      message: 'Please check your choices and try again.',
      reference: error.correlationId,
    };
  }
  return describeError(error);
}
